"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: "What sets our industrial solutions apart from the competition?",
    answer: "We combine decades of engineering precision with modern material science. Our approach focuses on durability, efficiency, and long-term performance, ensuring that every component we manufacture meets the highest standards of industrial reliability."
  }, {
    question: "Are your materials certified for high-stress environments?",
    answer: "Yes. All our materials undergo rigorous testing to ensure they meet international safety and performance standards. Each product specification sheet details the material composition, load-bearing capacity, and environmental resistance ratings."
  }, {
    question: "How do I select the right material for my specific application?",
    answer: "Our technical team is available to assist with material selection based on your project requirements. We analyze your operational environment, stress factors, and longevity goals to recommend the optimal solution for your needs."
  }, {
    question: "Do you provide global shipping and logistics support?",
    answer: "We manage a robust global supply chain, delivering to industrial hubs across North America, Europe, and Asia. Our logistics team ensures secure packaging and timely delivery, providing full tracking and documentation for every shipment."
  }, {
    question: "What is your policy on technical support and returns?",
    answer: "We stand behind our engineering. If a product does not meet your technical requirements, we offer a comprehensive review and return process within 45 days. Our support engineers are dedicated to resolving any integration challenges you may face."
  }, {
    question: "Can I access your technical documentation without a formal quote?",
    answer: "Certainly. Our technical library is open to all partners. Simply register your details in our portal to gain immediate access to CAD files, material data sheets, and performance white papers. No purchase commitment is required."
  }];
  return <section data-section-id="1474" id="faq" className="relative overflow-hidden bg-background text-foreground py-24 md:py-36">
      {}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-primary" />
      <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-2">
        <span className="block h-2 w-2 rounded-lg bg-primary" />
        <span className="block h-2 w-2 rounded-lg bg-primary" />
        <span className="block h-2 w-2 rounded-lg bg-primary" />
      </div>

      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative mx-auto max-w-[920px] px-6 md:px-14 transition-all duration-[620ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-6 md:-top-12 select-none font-default font-black leading-none text-foreground/[0.08] text-[10rem] md:text-[16rem] tracking-tight">
          06
        </div>

        {}
        <div className="relative text-center">
          {}
          <div className="inline-flex -rotate-6 items-center gap-2 rounded-lg bg-primary px-5 py-2 shadow-md">
            <span className="font-mono text-[0.72rem] font-medium uppercase tracking-[0.18em] text-primary-foreground">
              ◉ Track 06 — Technical Support
            </span>
          </div>

          <h2 className="mt-8 font-default font-extrabold text-foreground leading-[0.95] tracking-tight text-[clamp(2.1rem,5.2vw,4.5rem)]">
            Technical{" "}
            <span className="relative inline-block italic text-primary">
              Insights
              <svg aria-hidden="true" viewBox="0 0 120 12" className="absolute -bottom-3 left-0 w-full h-3" preserveAspectRatio="none">
                <path d="M2 8 Q 17 1, 32 6 T 62 6 T 92 6 T 118 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </span>{" "}
            explained…
          </h2>

          <p className="mx-auto mt-8 max-w-[620px] font-default text-foreground/80 text-[clamp(1.05rem,1.2vw,1.2rem)] leading-relaxed">
            Common inquiries regarding our engineering processes and material standards, addressed with the precision and transparency our partners expect.
          </p>
        </div>

        {}
        <div className="relative mt-16 md:mt-20">
          {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return <div key={i} data-index={i} className="relative border-b border-border last:border-b-0">
                {}
                <span aria-hidden="true" className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] bg-primary transition-all duration-[320ms] ease-out ${isOpen ? "h-1/2 opacity-100" : "h-0 opacity-0"}`} />

                <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} className="group flex w-full items-center justify-between gap-6 py-7 pl-5 pr-1 text-left transition-colors">
                  <h4 className="font-default font-semibold text-foreground text-[clamp(1.15rem,1.6vw,1.4rem)] leading-snug">
                    {faq.question}
                  </h4>
                  <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center text-primary transition-transform duration-[240ms] ease-out ${isOpen ? "rotate-45" : "rotate-0"}`} aria-hidden="true">
                    <Plus className="h-6 w-6" strokeWidth={2.25} />
                  </span>
                </button>

                <div className={`grid overflow-hidden transition-all duration-[360ms] ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="min-h-0">
                    <p className="pb-7 pl-5 pr-10 font-default text-foreground/75 text-[clamp(0.95rem,1vw,1.02rem)] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {}
        
      </div>
    </section>;
}